import React, { useState, useEffect, createContext } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BottomSheetModalProvider } from './src/components/BottomSheet';
import {
  StyleSheet,
  View,
  Platform,
  StatusBar,
  TextInput,
  Text,
  Dimensions,
} from 'react-native';
import { LogBox } from 'react-native';
import {
  LinkingOptions,
  NavigationContainer,
  useNavigationContainerRef,
  DefaultTheme,
} from '@react-navigation/native';
import MeanderStack from './src/components/MeanderStack';
import type { RootStackParamList } from './src/navigation/stackTypes';
import { MenuProvider } from './src/navigation/MenuContext';
import { SideMenu, EdgeSwipeGestureHandler } from './src/components/menu';
import { Provider as PaperProvider } from 'react-native-paper';
import { registerTranslation } from 'react-native-paper-dates';
import en from 'react-native-paper-dates/src/translations/en';
registerTranslation('en', en);
import { Provider as StoreProvider } from 'react-redux';
import store from './src/store';
import RoleService from './src/services/role-service';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { AutocompleteDropdownContextProvider } from 'react-native-autocomplete-dropdown';
import Toast, {
  BaseToast,
  ErrorToast,
  InfoToast,
} from 'react-native-toast-message';
import { TouchableOpacity } from 'react-native';
import { notificationService, NotificationData } from './src/services/notification-service';
import ExpoNotificationService from './src/services/ExpoNotificationService';
import NotificationNavigationHandler from './src/services/NotificationNavigationHandler';
import { liveActivityService } from './src/services/LiveActivityService';

// Import debugger in development
if (__DEV__) {
  require('./src/services/PushNotificationDebugger');
}

// Silence library-generated warning caused by modal dropdown internals
try { LogBox.ignoreLogs(['useInsertionEffect must not schedule updates']); } catch {}
import PopupModal from './src/components/PopupModal';
import { ThemedAlertProvider } from './src/components/common/ThemedAlert';
import ExpandableToastManager from './src/components/ExpandableToastManager';

import * as Font from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';

import { NativeWindStyleSheet } from 'nativewind'; // Import NativeWindStyleSheet
// ActiveChargeWrapper removed - active charge now accessible via side menu
// Archived to src/pages/Archive/ActiveChargeWrapper.tsx
import ChargeAgainWrapper from './src/pages/ChargeAgainWrapper';
import { routeTitleMap } from './src/components/common/utils/route-title-map';
// @ts-ignore - platform-specific exports resolved at runtime
import StripeProviderWrapper from './src/components/StripeProviderWrapper';
import HostModeWrapper from './src/components/HostModeWrapper';
import { RouteNameContext } from './src/navigation/RouteNameContext';
import { ChargeAgainContext } from './src/navigation/ChargeAgainContext';
import MandatoryVersionCheck from './src/components/MandatoryVersionCheck';
// Web-specific components
import { WebAppWrapper } from './src/components/web';
// Use require to avoid TS module resolution issues for assets
const PlusJakartaSansRegular = require('./assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Regular.ttf');
const PlusJakartaSansBold = require('./assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Bold.ttf');
const PlusJakartaSansMedium = require('./assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Medium.ttf');
const PoppinsBold = require('./assets/fonts/Poppins/Poppins-Bold.ttf');
const RobotoCondensedMedium = require('./assets/fonts/Roboto_Condensed/static/RobotoCondensed-Medium.ttf');
const RobotoCondensedBold = require('./assets/fonts/Roboto_Condensed/static/RobotoCondensed-Bold.ttf');
const RobotoCondensedRegular = require('./assets/fonts/Roboto_Condensed/static/RobotoCondensed-Regular.ttf');


NativeWindStyleSheet.setOutput({
  default: 'native',
});

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

// Set the animation options. This is optional.
SplashScreen.setOptions({
  duration: 1000,
  fade: true,
});

// Define routes and corresponding paths
const linking: LinkingOptions<RootStackParamList> = {
  //prefixes: ['meanderapp://'], // deep link prefixes
  prefixes: [`${process.env.REACT_NATIVE_API_SERVER}`],
  //prefixes: ['http://localhost'],
  config: {
    screens: {
      //OAuthRedirect: 'oauthSuccess',
      ChargerScan: 'chargerscan/:chargerSerial',
      OnboardingStatus: 'onboardingstatus',
      resetPassword: {
        path: 'reset-password',
        // If you want to pass the token as a param:
        // path: 'reset-password?token=:token'
      },
    },
  },
};

interface NavigationContextProps {
  currentRouteName: string | undefined;
  setCurrentRouteName: React.Dispatch<React.SetStateAction<string | undefined>>;
  showChargeAgain: boolean;
  setShowChargeAgain: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NavigationContext = createContext<
  NavigationContextProps | undefined
>(undefined);

// suppress console logging in production
if (process.env.REACT_NATIVE_ENV === 'production') {
  console.log = () => {};
}

// Helper function to calculate dynamic toast height
const calculateToastHeight = (message: string) => {
  const baseHeight = 60;
  const lineHeight = 18;
  const avgCharPerLine = 45; // Approximate characters per line considering toast width
  
  const estimatedLines = Math.ceil(message.length / avgCharPerLine);
  return Math.min(baseHeight + (estimatedLines * lineHeight), 200); // Max 200px
};

// Helper function to handle expandable error display
const handleExpandableError = (notificationData: NotificationData, 
  setErrorModalData: (data: NotificationData) => void, 
  setErrorModalVisible: (visible: boolean) => void) => {
  // console.log('handleExpandableError called with:', notificationData);
  if (notificationData.isExpandable) {
    // console.log('Opening modal with full message:', notificationData.fullMessage);
    setErrorModalData(notificationData);
    setErrorModalVisible(true);
  } else {
    // console.log('Error is not expandable');
  }
};

// Global reference to current notification for toast access
let currentNotificationRef: NotificationData | null = null;

const App = () => {
  // States and refs
  const navigationRef = useNavigationContainerRef<RootStackParamList>();
  const [currentRouteName, setCurrentRouteName] = useState<string | undefined>(
    undefined
  );
  // const [activeChargeOffset, setActiveChargeOffset] = useState<number>(0); // No longer needed
  const [showChargeAgain, setShowChargeAgain] = useState<boolean>(false);
  const [notification, setNotification] = React.useState<NotificationData>({ 
    type: 'default', 
    message: '', 
    isExpandable: false 
  });
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalData, setErrorModalData] = useState<NotificationData | null>(null);

  // Load fonts for the app

  const [fontsLoaded, error] = Font.useFonts({
    'PlusJakartaSans-Regular': PlusJakartaSansRegular,
    'PlusJakartaSans-Bold': PlusJakartaSansBold,
    'PlusJakartaSans-Medium': PlusJakartaSansMedium,
    'Poppins-Bold': PoppinsBold,
    'RobotoCondensed-Medium': RobotoCondensedMedium,
    'RobotoCondensed-Bold': RobotoCondensedBold,
    'RobotoCondensed-Regular': RobotoCondensedRegular,
  });

  // For disabling font scaling
  // @ts-ignore
  Text.defaultProps = { allowFontScaling: false };
  // @ts-ignore
  TextInput.defaultProps = { allowFontScaling: false };

  // Custom Toast configuration with dynamic height and expandable functionality
  const toastConfig = {
    success: (props: any) => {
      const dynamicHeight = calculateToastHeight(props.text1 + (props.text2 || ''));
      const screenWidth = Dimensions.get('window').width;
      return (
        <BaseToast
          {...props}
          style={{
            borderLeftColor: '#51B868',
            backgroundColor: '#23262B',
            minHeight: 60,
            maxHeight: dynamicHeight,
            width: Math.min(screenWidth - 40, 400), // Fixed width matching expandable toasts
          }}
          contentContainerStyle={{
            paddingHorizontal: 15,
            paddingVertical: 10,
          }}
          text1Style={{
            fontSize: 14,
            fontWeight: '600',
            color: '#FFFFFF',
            lineHeight: 18,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
          text2Style={{
            fontSize: 12,
            color: '#8D8E90',
            lineHeight: 16,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
        />
      );
    },
    error: (props: any) => {
      const dynamicHeight = calculateToastHeight(props.text1 + (props.text2 || ''));
      const screenWidth = Dimensions.get('window').width;
      const notificationData = currentNotificationRef;
      const isExpandable = notificationData?.isExpandable;
      
      // Debug logging
      // console.log('Toast error props:', { 
      //   hasCurrentRef: !!currentNotificationRef,
      //   notificationData, 
      //   isExpandable,
      //   text1: props.text1,
      //   text2: props.text2
      // });
      
      // Modify text2 to add "Tap for details" if expandable
      const modifiedProps = {
        ...props,
        text2: isExpandable ? `${props.text2 || ''} • Tap for details` : props.text2
      };

      return (
        <ErrorToast
          {...modifiedProps}
          style={{
            borderLeftColor: '#F97777',
            backgroundColor: '#23262B',
            minHeight: 60,
            maxHeight: dynamicHeight,
            width: Math.min(screenWidth - 40, 400), // Fixed width matching expandable toasts
            opacity: isExpandable ? 0.9 : 1, // Slightly transparent if expandable
          }}
          contentContainerStyle={{
            paddingHorizontal: 15,
            paddingVertical: 10,
          }}
          text1Style={{
            fontSize: 14,
            fontWeight: '600',
            color: '#FFFFFF',
            lineHeight: 18,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
          text2Style={{
            fontSize: 12,
            color: '#8D8E90',
            lineHeight: 16,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
        />
      );
    },
    info: (props: any) => {
      const dynamicHeight = calculateToastHeight(props.text1 + (props.text2 || ''));
      const screenWidth = Dimensions.get('window').width;
      return (
        <InfoToast
          {...props}
          style={{
            borderLeftColor: '#39BEB7',
            backgroundColor: '#23262B',
            minHeight: 60,
            maxHeight: dynamicHeight,
            width: Math.min(screenWidth - 40, 400), // Fixed width matching expandable toasts
          }}
          contentContainerStyle={{
            paddingHorizontal: 15,
            paddingVertical: 10,
          }}
          text1Style={{
            fontSize: 14,
            fontWeight: '600',
            color: '#FFFFFF',
            lineHeight: 18,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
          text2Style={{
            fontSize: 12,
            color: '#8D8E90',
            lineHeight: 16,
            flexWrap: 'wrap',
            flexShrink: 1,
          }}
        />
      );
    },
  };


  /**
   * Callback function that is executed when the navigation container is ready.
   * It retrieves the current route name from the navigation reference and updates the state with this route name.
   */
  const onReady = () => {
    const routeName = navigationRef.getCurrentRoute()?.name;
    setCurrentRouteName(routeName);
  };

  // Menu visibility is handled by the SideMenu component itself

  // Subscribe to notifications
  useEffect(() => {
    const subscription = notificationService.getNotification().subscribe((notificationData) => {
      if (notificationData) {
        //console.log('Received notification:', notificationData);
        currentNotificationRef = notificationData; // Store reference for toast access
        setNotification(notificationData);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    
    const initializeNotifications = async () => {
      try {
        // CRITICAL: Clean up any orphaned Live Activities on app launch
        // This handles the case where a charge completed while the app was in background
        // and the Live Activity wasn't properly cleaned up
        if (Platform.OS === 'ios') {
          try {
            const state = store.getState();
            const activeCharges = state.user?.activeCharges || [];
            const activeOrderIds = activeCharges.map((charge: any) => charge?.order?.id).filter(Boolean);
            
            // Clean up any Live Activities that don't match active charges
            await liveActivityService.cleanupOrphanedActivities(activeOrderIds);
          } catch (cleanupError) {
            // Don't let cleanup errors prevent app initialization
            console.warn('[App] Error during Live Activity cleanup:', cleanupError);
          }
        }
        
        // Set up notification listeners (keep Expo for UI handling)
        ExpoNotificationService.setupNotificationListeners();
        
        // Enable token refresh handling so changed APNs/Expo tokens re-register
        ExpoNotificationService.setupTokenRefreshHandling();
        
        // Set custom handlers for notifications
        ExpoNotificationService.onForegroundNotification((notification) => {
          // console.log('Custom foreground notification handler:', notification);
          const { title, body, data } = notification.request.content;
          
          // Show toast notification
          if (title || body) {
            notificationService.sendNotification('success', title || 'Notification', body || '');
          }
          
          // Handle Live Activity for charging notifications (iOS only)
          // This starts the Dynamic Island widget even when user is in the app
          // Wrap in try/catch to prevent crashes from notification handling errors
          if (data) {
            try {
              NotificationNavigationHandler.handleForegroundNotification(data as any);
            } catch (notifError) {
              console.warn('[App] Error handling foreground notification:', notifError);
            }
          }
        });

        ExpoNotificationService.onNotificationResponse(async (response) => {
          // console.log('Custom notification response handler:', response);
          try {
            const data = response.notification.request.content.data;
            await ExpoNotificationService.setBadgeCount(0);
            // Wrap in try/catch to prevent crashes from navigation errors
            await NotificationNavigationHandler.navigateFromNotification(data);
          } catch (responseError) {
            console.warn('[App] Error handling notification response:', responseError);
          }
        });

        // Check for initial notification (app opened from notification)
        await ExpoNotificationService.getInitialNotification();
        await ExpoNotificationService.setBadgeCount(0);

        // Request permissions and get tokens if user is authenticated
        const hasPermission = await ExpoNotificationService.requestPermissions();
        
        if (hasPermission) {
          // Service will get userId from AsyncStorage and handle authentication check
          await ExpoNotificationService.getAndRegisterToken();
        }

        // console.log('Push notifications initialized successfully');
      } catch (error) {
        console.error('Error initializing push notifications:', error);
      }
    };

    initializeNotifications();

    // Cleanup on unmount
    return () => {
      ExpoNotificationService.cleanup();
    };
  }, []);

  // Register push tokens when user authentication changes
  useEffect(() => {
    if (Platform.OS === 'web') return;

    let lastUserId: string | null = null;

    const registerTokensForAuthenticatedUser = async () => {
      try {
        const state = store.getState();
        const currentUserId = state.user?.session?.id || null;
        
        // Only register if user is authenticated and not a guest
        if (currentUserId && 
            currentUserId !== lastUserId && 
            currentUserId !== 'anonymous-user' && 
            !RoleService.isGuest(state.user?.session)) {
          
          // console.log('User authenticated, registering push tokens:', { userId: currentUserId, role: userRole });
          lastUserId = currentUserId;
          
          // console.log('User ID changed, re-registering push tokens:', currentUserId);
          await ExpoNotificationService.getAndRegisterToken(currentUserId);
        } else if (currentUserId !== lastUserId && (!currentUserId || RoleService.isGuest(state.user?.session))) {
          // User logged out or became guest - clear registration cache
          // console.log('User logged out or became guest, clearing push registration cache');
          lastUserId = null;
          await ExpoNotificationService.clearRegistrationCache();
        }
      } catch (error) {
        console.error('Error handling push token registration for user change:', error);
      }
    };

    // Subscribe to store changes but only act on user ID changes
    const unsubscribe = store.subscribe(() => {
      registerTokensForAuthenticatedUser();
    });

    return unsubscribe;
  }, []);

  // Toast notification - use custom expandable toast for expandable messages, regular toast for others
  useEffect(() => {
    if (
      notification.message &&
      typeof notification.message === 'string' &&
      notification.message.trim() !== ''
    ) {
      // For expandable messages, let the ExpandableToastManager handle it
      if (notification.isExpandable) {
        // console.log('Skipping regular toast for expandable notification');
        return;
      }

      // For non-expandable messages, use the regular toast
      const message = notification.message.trim();
      let text1 = message;
      let text2 = '';

      // If message is longer than 80 characters, split it
      if (message.length > 80) {
        const words = message.split(' ');
        let currentLength = 0;
        let titleWords: string[] = [];
        let subtitleWords: string[] = [];

        for (const word of words) {
          if (currentLength + word.length + 1 <= 80) {
            titleWords.push(word);
            currentLength += word.length + 1;
          } else {
            subtitleWords.push(word);
          }
        }

        text1 = titleWords.join(' ');
        text2 = subtitleWords.join(' ');
      }

      Toast.show({
        type: notification.type,
        text1: text1,
        text2: text2,
        visibilityTime: 4000, // Show for 4 seconds
        autoHide: true,
        topOffset: 50,
      });
    }
  }, [notification]);

  // useEffect to update the browser's page title based on the current route
  useEffect(() => {
    if (currentRouteName) {
      const routeDetails =
        routeTitleMap[currentRouteName] || routeTitleMap['undefined'];
      // document.title = routeDetails.title;
      // setActiveChargeOffset(routeDetails.offset); // No longer needed - active charge in side menu
    }
  }, [currentRouteName]); // Trigger the effect whenever currentRouteName changes

  // Hide the splash screen once resources are loaded
  useEffect(() => {
    if (fontsLoaded || error) {
      SplashScreen.hide();
    }
  }, [fontsLoaded, error]);

  // Skip rendering the app until the fonts are loaded
  if (!fontsLoaded && !error) {
    return null;
  }

  const navTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: '#1A1D21',
      card: '#1A1D21',
      border: '#1A1D21',
      primary: '#FFFFFF',
      text: '#FFFFFF',
      notification: '#1A1D21',
    },
  };

  return (
    <StoreProvider store={store}>
      <MandatoryVersionCheck>
        <GestureHandlerRootView style={styles.container} key={`GHRV`}>
          <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
          <SafeAreaProvider style={{ flex: 1, backgroundColor: '#1A1D21' }}>
          <BottomSheetModalProvider>
            <AutocompleteDropdownContextProvider>
              <PaperProvider>
              <RouteNameContext.Provider value={currentRouteName}>
                <ChargeAgainContext.Provider value={{ showChargeAgain, setShowChargeAgain }}>
                <StripeProviderWrapper>
                <MenuProvider>
                <ThemedAlertProvider>
                  <NavigationContainer
                    ref={navigationRef}
                    onReady={onReady}
                    onStateChange={() => {
                      const routeName = navigationRef.getCurrentRoute()?.name;
                      setCurrentRouteName(routeName);
                    }}
                    linking={linking}
                    theme={navTheme}
                  >
                    {/* Web wrapper provides centered container with proper boundaries */}
                    {Platform.OS === 'web' ? (
                      <WebAppWrapper>
                        <ChargeAgainWrapper
                          currentRouteName={currentRouteName}
                          showChargeAgain={showChargeAgain}
                          onDismiss={() => setShowChargeAgain(false)}
                        >
                          <HostModeWrapper>
                            <MeanderStack currentRouteName={currentRouteName ?? 'MeanderEV'} />
                          </HostModeWrapper>
                        </ChargeAgainWrapper>
                        {/* Side Menu - slides in from left */}
                        <SideMenu />
                      </WebAppWrapper>
                    ) : (
                      <>
                        <ChargeAgainWrapper
                          currentRouteName={currentRouteName}
                          showChargeAgain={showChargeAgain}
                          onDismiss={() => setShowChargeAgain(false)}
                        >
                          {/* ActiveChargeWrapper removed - active charge now accessible via side menu */}
                          <HostModeWrapper>
                            <MeanderStack currentRouteName={currentRouteName ?? 'MeanderEV'} />
                          </HostModeWrapper>
                        </ChargeAgainWrapper>
                        {/* Edge swipe gesture handler - opens menu on main pages */}
                        <EdgeSwipeGestureHandler currentRouteName={currentRouteName} />
                        {/* Side Menu - slides in from left */}
                        <SideMenu />
                        <StatusBar barStyle="light-content" backgroundColor="#1A1D21" />
                      </>
                    )}
                  </NavigationContainer>
                </ThemedAlertProvider>
                </MenuProvider>
                <Toast config={toastConfig} />
                
                {/* Custom Expandable Toast Manager */}
                <ExpandableToastManager notification={notification} />
                
                {/* Error Detail Modal */}
                <PopupModal
                  tooltipData={errorModalData ? {
                    header: "Error Details",
                    text: errorModalData.fullMessage || errorModalData.message
                  } : null}
                  visible={errorModalVisible}
                  buttons={[{
                    text: "Close",
                    onPress: () => setErrorModalVisible(false)
                  }]}
                  onClose={() => setErrorModalVisible(false)}
                />
              </StripeProviderWrapper>
              </ChargeAgainContext.Provider>
            </RouteNameContext.Provider>
            </PaperProvider>
          </AutocompleteDropdownContextProvider>
          </BottomSheetModalProvider>
          </SafeAreaProvider>
          </View>
        </GestureHandlerRootView>
      </MandatoryVersionCheck>
    </StoreProvider>
  );
};

const styles = StyleSheet.create({
  container: Platform.select({
    web: {
      flex: 1,
      backgroundColor: '#1A1D21',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      textAlign: 'center',
      marginTop: 0,
      marginBottom: 0,
    },
    default: {
      flex: 1,
      backgroundColor: '#1A1D21',
    },
  }),
});

export default App;
